/**
 * Sprint F (Feb 2026) — Read-only Welt-Anzeige im Mitarbeiter-Nachschlagewerk.
 *
 * Visuelle Hochstufung (Feb 2026 Pilot-Feedback): identisch zur Unternehmer-
 * Welt-Detailansicht `pages/Welt.jsx` — weiße SectionCards mit Icon-Header,
 * gefärbte Restrisiko-Badges, Label-Spalten-Layout für Maßnahmen. Statt
 * grauem Wireframe jetzt vollwertige Prüfakte-Optik.
 *
 * Unterschiede zur Unternehmer-Welt:
 * - Daten kommen aus dem Live-Endpoint `/me/worlds/{id}` (nicht aus Snapshot).
 * - Fragen werden OHNE `correct_index` angezeigt — Mitarbeiter sollen sich
 *   selbst prüfen können, nicht die Antwort ablesen.
 * - Keine Bearbeiten-/Freigeben-/Zuweisen-Buttons.
 * - Mobile-first (Cards bleiben weiß, padding angepasst).
 *
 * CLARTEXT SPRACHE V1 – Phase 2: NUR UI-Rahmen/Labels über t(). Die
 * eigentlichen Weltinhalte (Titel, Beschreibungen, Gefährdungen, Maßnahmen-
 * Texte, Fragen, Optionen, Wartungseinträge) sind Daten und bleiben deutsch.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useMonteurT, translateContentHeading } from "@/i18n/monteur";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { VisualObjects } from "@/components/VisualObjects";
import { RichtigFalschRenderer } from "@/vsi/renderers";
import { buildRichtigFalsch } from "@/vsi/richtigFalsch";
import { setActiveLehrbildWorld, setLehrbildMonteurKontext } from "@/vsi/lehrbild";
import {
  ArrowLeft,
  Loader2,
  ShieldAlert,
  FileWarning,
  BookOpen,
  HelpCircle,
  Wrench,
  RotateCw,
  Eye,
} from "lucide-react";

const RESIDUAL_RISK_COLOR = {
  gering: "bg-emerald-50 text-emerald-800 border-emerald-200",
  mittel: "bg-amber-50 text-amber-800 border-amber-200",
  hoch: "bg-rose-50 text-rose-800 border-rose-200",
};

// IMPLEMENT-18 (Juni 2026): identische Anzeige-Reihenfolge der Visual Learning
// Objects wie in der Unternehmer-Weltansicht (`pages/Welt.jsx`). Rein informativ,
// fail-closed: fehlt/leer/ungültig -> Renderer liefert null, der Eintrag entfällt.
const VLO_DISPLAY_ORDER = {
  learning_focus: 1,
  hazard_markers: 2,
  psa_summary: 3,
  safety_symbols: 4,
};

function recurrenceLabel(t, v) {
  if (!v) return t("welt.recurrence.none");
  const key = `welt.recurrence.${v}`;
  const label = t(key);
  // Unbekannte Enum-Werte: Rohwert zeigen (entspricht früherem Verhalten).
  return label === key ? v : label;
}

// ---------------------------------------------------------------------------
// Section-Container — wie in Welt.jsx (Unternehmer-Vorlage)
// ---------------------------------------------------------------------------

function SectionCard({ icon: Icon, title, subtitle, testId, children }) {
  return (
    <Card
      className="bg-white border border-slate-200 shadow-sm overflow-hidden"
      data-testid={testId}
    >
      <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      <div className="px-5 py-5">{children}</div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Haupt-Komponente
// ---------------------------------------------------------------------------

export default function MonteurWelt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // IMPLEMENT-18: QR-Modus (`?qr=1`) – Mitarbeiter kommt vom Plakat-Scan direkt
  // in diese Welt. Dann werden die Verständnisfragen ausgeblendet (reiner
  // visueller Nachschlage-/Unterweisungs-Blick, kein Selbsttest).
  const qrMode = searchParams.get("qr") === "1";
  const t = useMonteurT();
  const [welt, setWelt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .getMyWorld(id)
      .then((data) => {
        if (alive) setWelt(data);
      })
      .catch((err) => {
        if (!alive) return;
        const status = err?.response?.status;
        setError(
          status === 404
            ? t("welt.errorNotAvailable")
            : t("welt.errorLoad")
        );
      });
    return () => {
      alive = false;
    };
  }, [id, t]);

  // IMPLEMENT-18: aktive Welt für das KI-Lehrbild-Overlay setzen (lädt das
  // öffentliche Bild-Manifest – read-only, beweisneutral). Fehlen Bilder, rendern
  // die VLO-Kacheln fail-closed ohne Bild weiter.
  useEffect(() => {
    if (welt?.id) setActiveLehrbildWorld(welt.id);
  }, [welt?.id]);

  // IP2-D1C: Monteur-Renderkontext aktivieren (Mobile-Derivate bevorzugt,
  // Master-Fallback automatisch); beim Verlassen deterministisch zurücksetzen.
  useEffect(() => {
    setLehrbildMonteurKontext(true);
    return () => setLehrbildMonteurKontext(false);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header onBack={() => navigate(-1)} t={t} />
        <div
          className="max-w-2xl mx-auto px-5 py-12 text-center"
          data-testid="monteur-welt-error"
        >
          <p className="text-slate-600">{error}</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/monteur")}
            data-testid="monteur-welt-error-back"
          >
            {t("welt.backToTasks")}
          </Button>
        </div>
      </div>
    );
  }

  if (!welt) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header onBack={() => navigate(-1)} t={t} />
        <div className="max-w-2xl mx-auto px-5 py-12 flex items-center justify-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t("welt.loading")}
        </div>
      </div>
    );
  }

  const gbu = welt.gbu || {};
  const hazards = gbu.hazards || [];
  const ba = welt.betriebsanweisung || {};
  const mainHazards = ba.main_hazards || [];
  const protective = ba.protective_measures || [];
  const content = welt.content || [];
  const questions = welt.questions || [];
  const wartung = welt.wartung;
  const inspectionItems = wartung?.inspection_items || [];

  // IMPLEMENT-18: Visual Learning Objects (priorisiert & fail-closed) + Richtig/
  // Falsch – IDENTISCH zur Unternehmer-Weltansicht (`pages/Welt.jsx`). Reine
  // Anzeige aus bereits vorhandenen Daten (visual_objects, betriebsanweisung).
  const visualObjects = Array.isArray(welt.visual_objects)
    ? welt.visual_objects
        .filter((o) => o && VLO_DISPLAY_ORDER[o.vlo_type] != null)
        .sort(
          (a, b) => VLO_DISPLAY_ORDER[a.vlo_type] - VLO_DISPLAY_ORDER[b.vlo_type]
        )
    : [];
  const richtigFalsch = buildRichtigFalsch(welt);
  // AP-LANG-SOURCE: sprachneutrales deutsches Quell-Objekt (falls vom Backend additiv
  // mitgeliefert) für den sprachneutralen Lehrbild-Lookup; Anzeige bleibt übersetzt.
  // Guard: das native Course-Feld `source` ("ai_generated", String) wird ignoriert —
  // nur ein echtes Quell-OBJEKT wird verwendet.
  const weltSource =
    welt.source_display && typeof welt.source_display === "object"
      ? welt.source_display
      : null;
  const weltSourceRf = weltSource ? buildRichtigFalsch(weltSource) : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="monteur-welt">
      <Header onBack={() => navigate(-1)} t={t} />
      <article className="max-w-2xl mx-auto px-4 sm:px-5 py-6 space-y-5">
        {/* Titel-Karte: Eigenständig wertig, nicht Teil einer SectionCard. */}
        <Card className="bg-white border border-slate-200 shadow-sm px-5 py-6">
          <div
            className="text-xs uppercase tracking-[0.2em] text-slate-500"
            data-testid="monteur-welt-doctype"
          >
            {t("welt.doctype")}
          </div>
          <h1
            className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-1 leading-tight"
            data-testid="monteur-welt-title"
          >
            {welt.title}
          </h1>
          {welt.short_description && (
            <p className="text-sm text-slate-600 mt-2">
              {welt.short_description}
            </p>
          )}
          <div className="text-xs text-slate-400 mt-3 font-mono">
            {t("welt.versionLabel")} {welt.version}
          </div>
        </Card>

        {/* 1) Gefährdungsbeurteilung */}
        <SectionCard
          icon={ShieldAlert}
          title={t("welt.gbuTitle")}
          testId="monteur-welt-risk-assessment"
        >
          {hazards.length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              {t("welt.noHazards")}
            </p>
          ) : (
            <div className="space-y-3">
              {hazards.map((h, idx) => (
                <div
                  key={idx}
                  className="rounded-md border border-slate-200 p-4"
                  data-testid={`monteur-welt-hazard-${idx}`}
                >
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {h.category && (
                      <Badge
                        variant="outline"
                        className="bg-slate-100 border-slate-200 text-slate-700 text-[10px] uppercase tracking-wider"
                      >
                        {h.category}
                      </Badge>
                    )}
                    {h.residual_risk && (
                      <Badge
                        variant="outline"
                        className={`${
                          RESIDUAL_RISK_COLOR[h.residual_risk] ||
                          "bg-slate-50 border-slate-200 text-slate-700"
                        } text-[10px] uppercase tracking-wider`}
                      >
                        {t("welt.residualRisk")} {h.residual_risk}
                      </Badge>
                    )}
                  </div>
                  {h.description && (
                    <p className="text-sm text-slate-900 font-medium">
                      {h.description}
                    </p>
                  )}
                  <dl className="mt-3 space-y-2 text-sm">
                    {h.measures_technical && (
                      <div className="flex flex-col sm:flex-row sm:gap-2">
                        <dt className="text-xs uppercase tracking-wider text-slate-500 font-medium sm:w-32 shrink-0 sm:pt-0.5">
                          {t("welt.measureTechnical")}
                        </dt>
                        <dd className="text-slate-700">
                          {h.measures_technical}
                        </dd>
                      </div>
                    )}
                    {h.measures_organizational && (
                      <div className="flex flex-col sm:flex-row sm:gap-2">
                        <dt className="text-xs uppercase tracking-wider text-slate-500 font-medium sm:w-32 shrink-0 sm:pt-0.5">
                          {t("welt.measureOrganizational")}
                        </dt>
                        <dd className="text-slate-700">
                          {h.measures_organizational}
                        </dd>
                      </div>
                    )}
                    {h.measures_personal && (
                      <div className="flex flex-col sm:flex-row sm:gap-2">
                        <dt className="text-xs uppercase tracking-wider text-slate-500 font-medium sm:w-32 shrink-0 sm:pt-0.5">
                          {t("welt.measurePersonal")}
                        </dt>
                        <dd className="text-slate-700">{h.measures_personal}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              ))}
            </div>
          )}
          {gbu.responsible_role && (
            <p className="text-xs text-slate-500 mt-4">
              {t("welt.responsible")}{" "}
              <span className="font-medium">{gbu.responsible_role}</span>
            </p>
          )}
        </SectionCard>

        {/* 2) Betriebsanweisung */}
        {(ba.purpose ||
          mainHazards.length > 0 ||
          protective.length > 0 ||
          ba.behavior_in_emergency ||
          ba.first_aid ||
          ba.disposal_or_storage) && (
          <SectionCard
            icon={FileWarning}
            title={t("welt.baTitle")}
            subtitle={t("welt.baSubtitle")}
            testId="monteur-welt-operating-instruction"
          >
            {ba.purpose && (
              <p className="text-sm text-slate-700 mb-4">{ba.purpose}</p>
            )}

            {mainHazards.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                  {t("welt.mainHazards")}
                </h3>
                <ul className="space-y-1.5">
                  {mainHazards.map((h, i) => (
                    <li
                      key={i}
                      className="text-sm text-slate-700 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-slate-400"
                    >
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {protective.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                  {t("welt.protectiveMeasures")}
                </h3>
                <ul className="space-y-1.5">
                  {protective.map((m, i) => (
                    <li
                      key={i}
                      className="text-sm text-slate-700 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-slate-400"
                    >
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {ba.behavior_in_emergency && (
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1.5">
                    {t("welt.emergency")}
                  </h3>
                  <p className="text-sm text-slate-700">
                    {ba.behavior_in_emergency}
                  </p>
                </div>
              )}
              {ba.first_aid && (
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1.5">
                    {t("welt.firstAid")}
                  </h3>
                  <p className="text-sm text-slate-700">{ba.first_aid}</p>
                </div>
              )}
              {ba.disposal_or_storage && (
                <div className="sm:col-span-2">
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1.5">
                    {welt.subject_type === "hazmat"
                      ? t("welt.disposalHazmat")
                      : welt.subject_type === "vehicle"
                        ? t("welt.disposalVehicle")
                        : t("welt.disposalDefault")}
                  </h3>
                  <p className="text-sm text-slate-700">
                    {ba.disposal_or_storage}
                  </p>
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {/* 3) Unterweisung */}
        {content.length > 0 && (
          <SectionCard
            icon={BookOpen}
            title={t("welt.instructionTitle")}
            subtitle={t("welt.instructionSubtitle")}
            testId="monteur-welt-instruction"
          >
            <div className="space-y-4">
              {content.map((block, idx) => (
                <div key={idx} data-testid={`monteur-welt-instruction-${idx}`}>
                  {block.heading && (
                    <h3 className="font-heading font-semibold text-slate-900 text-sm mb-1">
                      {translateContentHeading(t, block.heading)}
                    </h3>
                  )}
                  {block.body && (
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {block.body}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* 3b) Visuelle Lernhilfen — KI-Lehrbilder + VLOs (Lernfokus,
            Gefahrenmarkierungen, Richtig/Falsch, PSA, Sicherheitszeichen).
            IDENTISCH zur Unternehmer-Weltansicht (`pages/Welt.jsx`). Read-only,
            additiv, beweisneutral. Fail-closed: ohne VLOs entfällt der Abschnitt. */}
        {(visualObjects.length > 0 || richtigFalsch) && (
          <SectionCard
            icon={Eye}
            title={t("welt.visualTitle")}
            subtitle={t("welt.visualSubtitle")}
            testId="monteur-welt-visual-objects-section"
          >
            <div className="space-y-4" data-testid="monteur-welt-visual-objects">
              <VisualObjects objects={visualObjects} sourceObjects={weltSource?.visual_objects || null} />
              {richtigFalsch && (
                <RichtigFalschRenderer vlo={richtigFalsch} sourceVlo={weltSourceRf} />
              )}
            </div>
          </SectionCard>
        )}

        {/* 4) Verständnisfragen — OHNE korrekte Antwort markiert (Sprint F).
            IMPLEMENT-18: im QR-Modus (`?qr=1`) komplett ausgeblendet — der
            Plakat-Scan führt zum reinen visuellen Nachschlage-Blick. */}
        {!qrMode && questions.length > 0 && (
          <SectionCard
            icon={HelpCircle}
            title={t("welt.questionsTitle")}
            subtitle={t("welt.questionsSubtitle", { count: questions.length })}
            testId="monteur-welt-questions"
          >
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div
                  key={q.id || idx}
                  className="rounded-md border border-slate-200 p-4"
                  data-testid={`monteur-welt-question-${idx}`}
                >
                  <p className="text-sm font-medium text-slate-900 mb-2">
                    {idx + 1}. {q.text}
                  </p>
                  {Array.isArray(q.options) && (
                    <ul className="space-y-1">
                      {q.options.map((opt, i) => (
                        <li
                          key={i}
                          className="text-sm text-slate-600 pl-5 relative before:content-['◦'] before:absolute before:left-0 before:text-slate-400"
                        >
                          {opt}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* 5) Prüf- & Wartungshinweise */}
        {wartung && inspectionItems.length > 0 && (
          <SectionCard
            icon={Wrench}
            title={t("welt.maintenanceTitle")}
            subtitle={t("welt.maintenanceSubtitle")}
            testId="monteur-welt-maintenance"
          >
            <div className="space-y-2">
              {inspectionItems.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 rounded-md border border-slate-200 p-3"
                  data-testid={`monteur-welt-inspection-${idx}`}
                >
                  {item.interval && (
                    <Badge
                      variant="outline"
                      className="bg-slate-100 border-slate-200 text-slate-700 text-[10px] uppercase tracking-wider shrink-0"
                    >
                      {item.interval}
                    </Badge>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-900">{item.description}</p>
                    {item.who && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {t("welt.performedBy")} {item.who}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {wartung.next_check_recommended_in && (
              <p className="text-xs text-slate-500 mt-4">
                {t("welt.recommendedCheck")}{" "}
                <span className="font-medium">
                  {wartung.next_check_recommended_in}
                </span>
              </p>
            )}
          </SectionCard>
        )}

        {/* 6) Wiedervorlage-Zyklen */}
        <SectionCard
          icon={RotateCw}
          title={t("welt.recurrenceTitle")}
          subtitle={t("welt.recurrenceSubtitle")}
          testId="monteur-welt-recurrence"
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-700">
                {t("welt.recurrenceUnterweisung")}
              </span>
              <Badge
                variant="outline"
                className="bg-slate-100 border-slate-200 text-slate-800 text-xs"
                data-testid="monteur-welt-recurrence-unterweisung"
              >
                {recurrenceLabel(t, welt.recurrence)}
              </Badge>
            </div>
            {welt.gbu_recurrence && (
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-sm text-slate-700">
                  {t("welt.recurrenceGbuReview")}
                </span>
                <Badge
                  variant="outline"
                  className="bg-slate-100 border-slate-200 text-slate-800 text-xs"
                  data-testid="monteur-welt-recurrence-gbu"
                >
                  {recurrenceLabel(t, welt.gbu_recurrence)}
                </Badge>
              </div>
            )}
            {welt.wartung_recurrence && (
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-700">
                  {t("welt.recurrenceWartung")}
                </span>
                <Badge
                  variant="outline"
                  className="bg-slate-100 border-slate-200 text-slate-800 text-xs"
                  data-testid="monteur-welt-recurrence-wartung"
                >
                  {recurrenceLabel(t, welt.wartung_recurrence)}
                </Badge>
              </div>
            )}
          </div>
        </SectionCard>

        <div className="pt-2 pb-12">
          <Button
            variant="outline"
            className="w-full h-12 bg-white"
            onClick={() => navigate("/monteur")}
            data-testid="monteur-welt-back-bottom"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("welt.backToTasks")}
          </Button>
        </div>
      </article>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sticky Header
// ---------------------------------------------------------------------------

function Header({ onBack, t }) {
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
      <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
        <button
          onClick={onBack}
          className="text-slate-700 hover:text-slate-900 -ml-2 p-2"
          aria-label={t("welt.backAria")}
          data-testid="monteur-welt-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="text-sm font-semibold text-slate-900">
          {t("welt.headerTitle")}
        </div>
      </div>
    </header>
  );
}
